import React from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { apiFetch, sessionFetch } from '../api/http';
import { isSessionExpiredError, safeErrorMessage, toAppError } from '../utils/appErrors';

export interface ParsedBEOAddress {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

export interface ParsedBEORoom {
  name: string;
  setup_style?: string | null;
  capacity?: number | null;
  notes?: string | null;
}

export interface ParsedBEOContact {
  name?: string | null;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
}

export interface ParsedBEOLineItem {
  description: string;
  category?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  notes?: string | null;
  /** Product match (populated server-side by AI matching) */
  matched_product_id?: string | null;
  matched_product_code?: string | null;
  matched_product_name?: string | null;
  /** Resolved from product.default_item_id */
  matched_item_id?: string | null;
  matched_item_code?: string | null;
  match_confidence?: 'high' | 'medium' | 'low' | 'none' | null;
  /** True if the AI flagged this as a catch-all summary, not specific equipment */
  is_generic?: boolean;
}

export interface ParsedBEONumberEntry {
  date: string;
  beo_no: string;
}

/** A single event within a BEO packet. */
export interface ParsedBEOEvent {
  venue_name?: string | null;
  event_name?: string | null;
  beo_numbers?: ParsedBEONumberEntry[] | null;
  client_name?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  event_start_time?: string | null;
  event_end_time?: string | null;
  setup_date?: string | null;
  setup_time?: string | null;
  load_in_date?: string | null;
  load_in_time?: string | null;
  strike_date?: string | null;
  strike_time?: string | null;
  rooms?: ParsedBEORoom[] | null;
  contacts?: ParsedBEOContact[] | null;
  line_items?: ParsedBEOLineItem[] | null;
  anticipated_attendees?: number | null;
  guaranteed_count?: number | null;
  av_requirements?: string | null;
  catering_summary?: string | null;
  special_requirements?: string | null;
  estimated_total?: number | null;
  is_in_house?: boolean | null;
}

/** Info about page truncation when a PDF exceeds the render limit. */
export interface BEOTruncationInfo {
  pages_processed: number;
  pages_total: number;
}

/** Top-level BEO packet: shared venue + array of events. */
export interface ParsedBEOPacket {
  venue_name?: string | null;
  venue_address?: ParsedBEOAddress | null;
  events: ParsedBEOEvent[];
  truncated?: BEOTruncationInfo | null;
}

export function useBEOScan(_supabase?: SupabaseClient) {
  void _supabase;
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const scan = React.useCallback(async (file: File, model?: string): Promise<ParsedBEOPacket | null> => {
    setLoading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (model) form.append('model', model);
      // Prefer proxy path; fallback to direct backend if proxy aborts/resets
      let resp: Response | null = null;
      try {
        resp = await apiFetch('/admin/ai/scan-beo', { method: 'POST', body: form });
      } catch (primaryError: unknown) {
        if (isSessionExpiredError(primaryError)) throw primaryError;
        try {
          const direct = (import.meta as any).env?.VITE_SERVER_URL || 'http://localhost:5178';
          resp = await sessionFetch(`${direct}/api/admin/ai/scan-beo`, {
            method: 'POST',
            body: form,
            mode: 'cors',
            credentials: 'omit',
          });
        } catch (fallbackError: unknown) {
          setError(safeErrorMessage(fallbackError, 'The BEO scan service is unavailable.'));
          return null;
        }
      }
      if (!resp) { setError('No response from scan service'); return null; }
      let json: any = null;
      try { json = await resp.json(); } catch (_e) { json = null; }
      if (!resp.ok) {
        setError(toAppError({ status: resp.status }, 'The BEO could not be scanned.', 'beo_scan').message);
        return null;
      }

      // Normalize legacy beo_number string → beo_numbers array
      function normalizeBeoNumbers(ev: any): any {
        if (ev.beo_numbers) return ev;
        if (ev.beo_number) {
          return { ...ev, beo_numbers: [{ date: ev.event_start_date || '', beo_no: ev.beo_number }] };
        }
        return ev;
      }

      // New multi-event shape: { venue_name, venue_address, events[], truncated? }
      if (Array.isArray(json?.events)) {
        return {
          venue_name: json.venue_name || null,
          venue_address: json.venue_address || null,
          events: json.events.map(normalizeBeoNumbers),
          truncated: json.truncated || null,
        };
      }
      // Legacy single-event fallback: { beo: {...} }
      if (json?.beo) {
        const beo = json.beo;
        return {
          venue_name: beo.venue_name || null,
          venue_address: beo.venue_address || null,
          events: [normalizeBeoNumbers(beo)],
        };
      }
      setError('Unexpected response format');
      return null;
    } catch (error: unknown) {
      setError(safeErrorMessage(error, 'The BEO could not be scanned.'));
      return null;
    }
    finally { setLoading(false); }
  }, []);

  /**
   * Start an async background BEO scan. Returns the jobId on success (null on error).
   * The server processes the file in the background and sends a notification when done.
   */
  const startScan = React.useCallback(async (file: File, model?: string): Promise<string | null> => {
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (model) form.append('model', model);
      // Prefer proxy path; fallback to direct backend
      let resp: Response | null = null;
      try {
        resp = await apiFetch('/admin/ai/scan-beo-start', { method: 'POST', body: form });
      } catch (primaryError: unknown) {
        if (isSessionExpiredError(primaryError)) throw primaryError;
        try {
          const direct = (import.meta as any).env?.VITE_SERVER_URL || 'http://localhost:5178';
          resp = await sessionFetch(`${direct}/api/admin/ai/scan-beo-start`, {
            method: 'POST',
            body: form,
            mode: 'cors',
            credentials: 'omit',
          });
        } catch (fallbackError: unknown) {
          setError(safeErrorMessage(fallbackError, 'The BEO import service is unavailable.'));
          return null;
        }
      }
      if (!resp) { setError('No response from scan service'); return null; }
      let json: any = null;
      try { json = await resp.json(); } catch (_e) { json = null; }
      if (!resp.ok) {
        setError(toAppError({ status: resp.status }, 'The BEO import could not be started.', 'beo_scan_start').message);
        return null;
      }
      return json?.jobId || null;
    } catch (error: unknown) {
      setError(safeErrorMessage(error, 'The BEO import could not be started.'));
      return null;
    }
  }, []);

  return { scan, startScan, loading, error };
}
